// packages/api-client/src/services/public/quotesService.ts
import { getApiClient } from '../../client';
import { handleApiError } from '../../utils/handleApiError';
import type {
  ServiceResponse,
  Quote,
  UUID,
  CreateTopupInvoiceResponse,
  AgentVersionOverrides,
  PaginatedResponse,
  QuoteSummary,
} from '@dopomogai/types';

const QUOTES_PREFIX = '/quotes';

interface QuoteCreatePayload {
  discount_code?: string;
  overrides?: Partial<AgentVersionOverrides>;
}

export const quotesService = {
  /**
   * Generates a new, signed quote from a specific agent version.
   */
  createQuote: (versionId: UUID, payload: QuoteCreatePayload = {}): ServiceResponse<Quote> => {
    return getApiClient()
      .post<Quote>(`${QUOTES_PREFIX}/versions/${versionId}/quote`, payload)
      .then(res => res.data)
      .catch(err => handleApiError(err, `creating quote for version '${versionId}'`));
  },

  /**
   * Fetch a specific, read-only quote by its ID.
   */
  getQuote: (quoteId: UUID): ServiceResponse<Quote> => {
    return getApiClient()
      .get<Quote>(`${QUOTES_PREFIX}/${quoteId}`)
      .then(res => res.data)
      .catch(err => handleApiError(err, `fetching quote '${quoteId}'`));
  },

  /**
   * Generate a Monobank payment link for a specific quote.
   */
  createPaymentLinkForQuote: (quoteId: UUID): ServiceResponse<CreateTopupInvoiceResponse> => {
    return getApiClient()
      .post<CreateTopupInvoiceResponse>(`${QUOTES_PREFIX}/${quoteId}/create-payment-link`)
      .then(res => res.data)
      .catch(err => handleApiError(err, `creating payment link for quote '${quoteId}'`));
  },

  /**
   * List the authenticated user's quotes.
   */
  listMyQuotes: (limit = 20, offset = 0): ServiceResponse<PaginatedResponse<QuoteSummary>> => {
    const qs = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    return getApiClient()
      .get<PaginatedResponse<QuoteSummary>>(`${QUOTES_PREFIX}/my-quotes?${qs.toString()}`)
      .then(res => res.data)
      .catch(err => handleApiError(err, 'fetching my quotes'));
  },
};
